import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { Loading } from "@/components/site/ui";
import { Panel, AdminHeading, adminInput } from "@/components/admin/ui";
import { Switch } from "@/components/ui/switch";
import { useAuth } from "@/context/AuthContext";
import OnlinePaymentSettings from "@/components/admin/OnlinePaymentSettings";

const FEATURES = [
  ["ai", "AI Concierge Assist", "AI-drafted messages & summaries (drafts only; you review & send)."],
  ["ambient_sound", "Ambient Sound Toggle", "Show the ambient sound control on the public site."],
  ["rosemary_experience", "Rosemary Experience", "Feature the signature rosemary experience."],
  ["guestbook", "Guestbook", "Allow guests to sign the digital guestbook."],
  ["weather", "Weather Module", "Live weather at The D (Kissimmee, FL). Active — no credentials required."],
  ["online_payments", "Online Payments", "Secure hosted card payments, requests, refunds and disputes (Sandbox Mode active)."],
  ["sms", "SMS Notifications", "Requires an SMS provider (architecture only)."],
];

const INTEGRATIONS = [
  ["Email (SMTP2GO)", "smtp", "Set SMTP_HOST, SMTP_USERNAME, SMTP_PASSWORD, SMTP_FROM_EMAIL in the server environment."],
  ["Online Payment Provider", "online", "Connected in Sandbox Mode. Managed server-side — no keys in the browser or source control."],
  ["Weather", "weather", "Live weather is active (Kissimmee, FL) via a keyless provider — no environment variables required."],
  ["SMS Provider", "sms", "Architecture prepared; no provider connected."],
];

export default function AdminSettings() {
  const { user } = useAuth();
  const isOwner = ["owner", "host"].includes(user?.role);
  const [s, setS] = useState(null);
  const [smtp, setSmtp] = useState(false);
  const load = () => api.get("/admin/settings").then((r) => { setS(r.data.settings); setSmtp(r.data.smtp_configured); }).catch(() => setS(false));
  useEffect(() => { load(); }, []);
  const set = (k, v) => setS((p) => ({ ...p, [k]: v }));
  const setFeature = (k, v) => setS((p) => ({ ...p, features: { ...p.features, [k]: v } }));
  const save = async () => { try { await api.put("/admin/settings", { data: s }); toast.success("Configuration saved."); } catch (e) { toast.error(apiErr(e)); } };

  if (s === null) return <Loading />;
  if (!s) return <p className="text-muted-foreground">Unable to load settings.</p>;

  const statusFor = (key) => key === "smtp" ? (smtp ? "Configured" : "Not Configured") : key === "online" ? "Connected — Sandbox Mode" : "Not Configured";

  return (
    <div className="max-w-4xl">
      <AdminHeading title="Configuration"><button onClick={save} className="btn-gold px-5 py-2.5 text-sm flex items-center gap-2" data-testid="settings-save"><Save size={15} /> Save</button></AdminHeading>
      <div className="space-y-6">
        <Panel title="Property">
          <div className="grid sm:grid-cols-2 gap-4">
            <input className={adminInput} value={s.property_name || ""} onChange={(e) => set("property_name", e.target.value)} placeholder="Property name" />
            <input className={adminInput} value={s.subtitle || ""} onChange={(e) => set("subtitle", e.target.value)} placeholder="Subtitle" />
            <input className={adminInput} value={s.checkin_time || ""} onChange={(e) => set("checkin_time", e.target.value)} placeholder="Check-in time" />
            <input className={adminInput} value={s.checkout_time || ""} onChange={(e) => set("checkout_time", e.target.value)} placeholder="Checkout time" />
            <input className={adminInput} value={s.confirmation_prefix || ""} onChange={(e) => set("confirmation_prefix", e.target.value)} placeholder="Confirmation prefix (e.g. TD)" />
            <input className={adminInput} value={s.host_contact || ""} onChange={(e) => set("host_contact", e.target.value)} placeholder="Host contact (internal)" />
            <input className={adminInput} value={s.public_contact_email || ""} onChange={(e) => set("public_contact_email", e.target.value)} placeholder="Public contact email" data-testid="settings-public-contact" />
          </div>
          <div className="flex items-center gap-4 mt-4">
            <label className="flex items-center gap-2 text-sm"><Switch checked={!!s.tax_enabled} onCheckedChange={(v) => set("tax_enabled", v)} /> Enable tax</label>
            {s.tax_enabled && <input type="number" className={adminInput + " w-28"} value={s.tax_percent || 0} onChange={(e) => set("tax_percent", +e.target.value)} placeholder="Tax %" />}
          </div>
        </Panel>

        <Panel title="Feature Toggles">
          <div className="space-y-3">{FEATURES.map(([k, label, desc]) => (
            <div key={k} className="flex items-center justify-between">
              <div><p className="text-sm">{label}</p><p className="text-xs text-muted-foreground">{desc}</p></div>
              <Switch checked={!!(s.features || {})[k]} onCheckedChange={(v) => setFeature(k, v)} data-testid={`feature-${k}`} />
            </div>
          ))}</div>
        </Panel>

        {isOwner && <OnlinePaymentSettings />}

        <Panel title="Integrations Status">
          <div className="space-y-3">{INTEGRATIONS.map(([label, key, note]) => (
            <div key={key} className="flex items-center justify-between py-2 border-b border-white/5">
              <div><p className="text-sm">{label}</p><p className="text-xs text-muted-foreground">{note}</p></div>
              <span className={`status-pill border ${(statusFor(key) === "Configured" || statusFor(key).startsWith("Connected")) ? "text-pool border-pool/40 bg-pool/10" : "text-muted-foreground border-white/20 bg-white/5"}`}>{statusFor(key)}</span>
            </div>
          ))}</div>
          <p className="text-[11px] text-muted-foreground mt-3">All credentials are stored server-side in environment variables — never in the browser or source control.</p>
        </Panel>
      </div>
    </div>
  );
}
